import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormSpy } from 'react-final-form';
import { useParams, useHistory } from 'react-router-dom';
import clsx from 'clsx';
import {
  Grid,
  Typography,
  Paper,
  MuiThemeProvider,
  createStyles,
  makeStyles,
  createTheme
} from '@material-ui/core';
import { renderErrorOrLoading, getInitialFieldValues } from 'components/Crud';
import useCrudStyles from 'components/Crud/styles';
import { AppDispatch, RootState } from 'store';
import { commonErrorHandling } from 'store/utils';
import { mergeErrors } from 'utils/utils';
import Snackbars from 'components/Alerts/Snackbars';
import {
  Form,
  FormValues,
  yupSchemaToValidationFunc,
  Field
} from 'components/Forms';
import { SecondaryButton, SubmitButton } from 'components/Forms/Buttons';
import { mapRequestDataToField } from 'components/Forms/utils/dataMap';
import {
  editAppointment,
  fetchAppointment,
  requestStart as appointmentsRequestStart
} from 'store/slices/appointments';
import {
  fetchAllEngineers,
  requestStart as engineersRequestStart
} from 'store/slices/engineers';
import defaultTheme, { modalFormInputOverrides } from 'styles/defaultTheme';
import { appointmentDomain } from 'models';
import Appointment from 'models/Appointment';
import { useAppointmentData } from 'store/selectors';
import { EngineersSelector, engineerSlice } from './store';
import {
  validationSchema,
  processFormFields,
  appointmentFormFields,
  AppointmentFormFieldsType
} from './formConfig';
import ConflictingEngineersBanner from './ConflictingEngineersBanner';
import { useAppointmentEngineerConflicts } from './useAppointmentEngineerConflicts';

const useStyles = makeStyles(
  theme =>
    createStyles({
      validationErrors: {
        width: theme.containers.small,
        marginBottom: theme.spacing(2)
      },
      pageTitle: {
        marginBottom: theme.spacing(4)
      },
      submitButton: {
        padding: theme.spacing(1, 2)
      },
      cancelButton: {
        marginLeft: theme.spacing(0.5)
      }
    }),
  { name: 'EditAppointmentView' }
);

function EditAppointmentView() {
  const dispatch = useDispatch<AppDispatch>();
  const classes = useStyles();
  const crudClasses = useCrudStyles();
  const history = useHistory();
  const { id: appointmentId } = useParams<{ id: string }>();

  // Force both slices into loading on the very first render so the form can't mount against stale data left over
  // from a prior view (like EngineerCalendar's fetchAllEngineerFixedAppointments dispatch)
  useState(() => {
    dispatch(engineersRequestStart());
    dispatch(appointmentsRequestStart());
    return null;
  });

  const appointmentsIsLoading = useSelector<RootState, boolean>(
    state => state.appointments.isLoading
  );

  const {
    appointmentConflicts,
    conflictedEngineerIds,
    selectedEngineerIds,
    onFormSpyChange,
    handleConflictResult
  } = useAppointmentEngineerConflicts();

  const engineerSelector = useSelector<RootState, EngineersSelector>(
    engineerSlice.selector
  );

  const validationFunc = yupSchemaToValidationFunc(validationSchema);

  const processAppointmentData = mapRequestDataToField({
    engineer: (appointment: Appointment) => appointment.engineers,
    date: (appointment: Appointment) => appointment.slot.date,
    startTime: ({ slot: { date, startTime } }: Appointment) =>
      `${date}T${startTime}`,
    endTime: ({ slot: { date, endTime } }: Appointment) => `${date}T${endTime}`,
    reason: (appointment: Appointment) => appointment.reason,
    postcode: (appointment: Appointment) => appointment.address?.postcode,
    notes: (appointment: Appointment) =>
      appointment.notes
        .reduce((acc, { note }) => [...acc, note], [] as string[])
        .join('\n')
  });

  const appointmentData = useAppointmentData(appointmentId);

  const onSubmit = async (fieldValues: FormValues): Promise<void> => {
    try {
      const processedFields = processFormFields(fieldValues);
      const conflicts = await dispatch(
        editAppointment(processedFields, appointmentId)
      );

      if (!conflicts?.length) {
        setTimeout(() => history.goBack(), 1000);
        return;
      }

      handleConflictResult(conflicts);
    } catch (e) {
      commonErrorHandling(e);
    }
  };

  const onFormCancel = () => {
    history.goBack();
  };

  useEffect(() => {
    dispatch(fetchAllEngineers(100));
    dispatch(fetchAppointment(appointmentId));
  }, []);

  const fields = useMemo(
    () =>
      appointmentFormFields(
        AppointmentFormFieldsType.Edit,
        engineerSelector.items,
        conflictedEngineerIds,
        selectedEngineerIds.length
      ),
    [engineerSelector.items, conflictedEngineerIds, selectedEngineerIds.length]
  );

  // Memoised so the prop has a stable reference across renders; otherwise
  // react-final-form treats every render as a fresh `initialize()` and wipes
  // user edits before submit.
  const initialValues = useMemo(
    () =>
      getInitialFieldValues(fields, appointmentData, processAppointmentData),
    [fields, appointmentData]
  );

  const hasLoadedInitialData = useRef(false);
  if (
    appointmentData &&
    !engineerSelector.isLoading &&
    !appointmentsIsLoading
  ) {
    hasLoadedInitialData.current = true;
  }

  const isInitialLoad =
    !hasLoadedInitialData.current &&
    (engineerSelector.isLoading || appointmentsIsLoading || !appointmentData);

  return (
    renderErrorOrLoading(
      isInitialLoad,
      'editAppointmentSpinner',
      mergeErrors([engineerSelector.error]),
      crudClasses
    ) || (
      <>
        <Grid container className={crudClasses.formContainer}>
          <Grid item xs={12}>
            <Typography
              variant="h1"
              className={classes.pageTitle}
              data-testid="editAppointmentPageTitle"
            >
              Edit {appointmentDomain.label}
            </Typography>
            {conflictedEngineerIds.length > 0 && (
              <ConflictingEngineersBanner
                conflictedEngineers={conflictedEngineerIds.length}
                totalEngineers={selectedEngineerIds.length}
                appointmentConflicts={appointmentConflicts}
                className={clsx(crudClasses.root, classes.validationErrors)}
              />
            )}
            <Paper className={`${crudClasses.root} ${crudClasses.paper}`}>
              <MuiThemeProvider
                theme={createTheme({
                  ...defaultTheme,
                  overrides: {
                    ...defaultTheme.overrides,
                    ...modalFormInputOverrides
                  }
                })}
              >
                <Form
                  validationFunc={validationFunc}
                  onSubmit={onSubmit}
                  initialValues={initialValues}
                  // protects user edits when a real reinit fires.
                  keepDirtyOnReinitialize
                  goBackToPreviousPage={false}
                >
                  {() => (
                    <>
                      <FormSpy
                        subscription={{
                          values: true,
                          submitFailed: true,
                          submitting: true
                        }}
                        onChange={onFormSpyChange}
                      />
                      {fields.map(field => (
                        <Field key={field.id} field={field} />
                      ))}
                      <SubmitButton
                        testId="createDomainSave"
                        className={classes.submitButton}
                      >
                        Schedule
                      </SubmitButton>
                      <SecondaryButton
                        onClick={onFormCancel}
                        size="medium"
                        className={classes.cancelButton}
                        testId="editAppointmentCancel"
                      >
                        Cancel
                      </SecondaryButton>
                    </>
                  )}
                </Form>
              </MuiThemeProvider>
            </Paper>
          </Grid>
        </Grid>
        <Snackbars />
      </>
    )
  );
}

export default EditAppointmentView;
